import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEOHead from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginHeader = () => (
  <div className="text-center mb-8">
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full mb-4">
      <Home className="w-5 h-5 text-emerald-400" />
      <span className="text-white font-bold">DreamHousesUSA</span>
    </div>
    <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
    <p className="text-slate-300 text-sm">Sign in to view leads and analytics</p>
  </div>
);

const PasswordInput = ({ value, onChange }) => (
  <div>
    <Label htmlFor="password" className="text-slate-700">Admin Password</Label>
    <div className="relative mt-1.5">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input
        id="password"
        data-testid="admin-password-input"
        type="password"
        required
        autoFocus
        value={value}
        onChange={onChange}
        placeholder="Enter admin password"
        className="pl-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
      />
    </div>
  </div>
);

const SubmitButton = ({ loading }) => (
  <Button
    type="submit"
    data-testid="admin-login-submit"
    disabled={loading}
    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6"
  >
    {loading ? (
      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</>
    ) : 'Sign In'}
  </Button>
);

const useLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (password) => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, submit };
};

const AdminLoginPage = () => {
  const [password, setPassword] = useState('');
  const { loading, error, submit } = useLogin();

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center px-4">
      <SEOHead title="Admin — DreamHousesUSA" description="Admin login" canonical="https://www.dreamhousesusa.com/admin" />
      <div className="w-full max-w-md">
        <LoginHeader />
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {error}
              </div>
            )}
            <SubmitButton loading={loading} />
          </form>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          Authorized personnel only • Activity is logged
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
